import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { supabase } from "../utils/supabaseClient";
import { checkEmailExists } from "../api/user";
import "../styles/main.css";

const USER_AGREEMENT_URL = process.env.REACT_APP_USER_AGREEMENT_URL || "https://example.com/user-agreement";
const PRIVACY_POLICY_URL = process.env.REACT_APP_PRIVACY_POLICY_URL || "https://example.com/privacy";

// Translation object
const translations = {
  en: {
    signUp: "Sign Up",
    logIn: "Log In",
    resetPassword: "Reset your password",
    bycontinuing: "By continuing, you agree to our",
    userAgreement: "User Agreement",
    andAcknowledge: "and acknowledge that you understand the",
    privacyPolicy: "Privacy Policy",
    email: "Email",
    password: "Password",
    continue: "Continue",
    sendResetLink: "Send reset link",
    forgotPassword: "Forgot password?",
    alreadyHaveAccount: "Already have an account?",
    rememberedPassword: "Remembered your password?",
    newToSite: "New to the site?",
    pleaseWait: "Please wait…",
    enterValidEmail: "Please enter a valid email.",
    passwordMinLength: "Password must be at least 6 characters.",
    signedIn: "Signed in",
    enterEmailAndPassword: "Please enter email and password.",
    closeLabel: "Close dialog",
    checkEmail: "Success! Please check your email for the confirmation link.",
    resetSent: "Reset link sent.",
    sendVerification: "Send Verification Link",
    verificationSent: "Verification link sent! Please check your email.",
    emailExists: "This email is already registered. Please log in.",
    invalidCredentials: "Invalid email or password.",
    resendLink: "Resend verification link",
    resendAvailableIn: "Available in",
    seconds: "s",
  },
  zh: {
    signUp: "注册",
    logIn: "登录",
    resetPassword: "重置密码",
    bycontinuing: "继续即表示您同意我们的",
    userAgreement: "用户协议",
    andAcknowledge: "并确认您理解",
    privacyPolicy: "隐私政策",
    email: "邮箱",
    password: "密码",
    continue: "继续",
    sendResetLink: "发送重置链接",
    forgotPassword: "忘记密码？",
    alreadyHaveAccount: "已有账号？",
    rememberedPassword: "想起密码了？",
    newToSite: "新用户？",
    pleaseWait: "请稍候…",
    enterValidEmail: "请输入有效的邮箱地址。",
    passwordMinLength: "密码长度至少为6个字符。",
    signedIn: "已登录",
    enterEmailAndPassword: "请输入邮箱和密码。",
    closeLabel: "关闭对话框",
    checkEmail: "成功！请检查您的邮箱以获取确认链接。",
    resetSent: "重置链接已发送。",
    sendVerification: "发送验证链接",
    verificationSent: "验证链接已发送！请检查您的邮箱。",
    emailExists: "该邮箱已被注册。请直接登录。",
    invalidCredentials: "邮箱或密码错误。",
    resendLink: "重新发送验证链接",
    resendAvailableIn: "可在",
    seconds: "秒后重发",
  }
};

export default function AuthPage({ open: propOpen = null, onClose = null, defaultMode: propMode = null }) {
  const initialMode = (propMode || "signin").toLowerCase();
  const [mode, setMode] = useState(initialMode); // signin | signup | reset
  const [open, setOpen] = useState(Boolean(propOpen ?? true));
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success | error
  const [resendCountdown, setResendCountdown] = useState(0);

  // Use language from context
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  useEffect(() => setMode(initialMode), [initialMode]);
  useEffect(() => { if (propOpen !== null) setOpen(Boolean(propOpen)); }, [propOpen]);

  // Clear message when switching modes
  useEffect(() => { 
    setMessage(""); 
    setMessageType("");
  }, [mode]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  function closeModal() {
    if (onClose) onClose();
    else setOpen(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      // --- 1. SIGN UP (Magic Link Flow) ---
      if (mode === "signup") {
        if (!email) {
            throw new Error(t.enterValidEmail);
        }

        // Check if email exists in USER_PROFILES table
        const exists = await checkEmailExists(email);
        if (exists) {
          throw new Error(t.emailExists);
        }

        // Debug: Log language being sent
        console.log("Sending signup email with language:", language);

        // Use signInWithOtp for "Sign Up" flow to verify email first
        const { error } = await supabase.auth.signInWithOtp({
          email: email,
          options: {
            emailRedirectTo: `${window.location.origin}/update-password?email=${encodeURIComponent(email)}`,
            data: {
              language: language,
            },
          },
        });
        
        if (error) throw error;

        setMessage(t.verificationSent);
        setMessageType("success");
        setResendCountdown(60); // Start 60 second countdown
      } 

      // --- 2. RESET PASSWORD ---
      else if (mode === "reset") {
        if (!email) throw new Error(t.enterValidEmail);

        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/update-password?email=${encodeURIComponent(email)}`,
        });
        
        if (error) throw error;
        
        setMessage(t.resetSent);
        setMessageType("success");
        setResendCountdown(60); // Start 60 second countdown
      } 

      // --- 3. SIGN IN ---
      else {
        if (!email || !password) throw new Error(t.enterEmailAndPassword);

        const { error } = await supabase.auth.signInWithPassword({
          email: email,
          password: password,
        });
        
        if (error) throw error;
        
        setMessage(t.signedIn);
        setMessageType("success");
        setTimeout(() => {
          closeModal();
          navigate('/dashboard');
        }, 500);
      }

    } catch (err) {
      let errorMsg = err.message;
      // Simple mapping for common Supabase errors
      if (errorMsg.includes("Invalid login credentials")) errorMsg = t.invalidCredentials;
      
      setMessage(errorMsg);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="auth-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="card auth-modal-card" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        
        {/* Close button */}
        <button 
          className="auth-close-btn"
          aria-label={t.closeLabel} 
          onClick={closeModal} 
        >
          ×
        </button>

        <h1 style={{ textAlign: "center", marginBottom: 6 }}>
          {mode === "signup" ? t.signUp : mode === "reset" ? t.resetPassword : t.logIn}
        </h1>

        {mode === "signin" && (
          <p className="auth-subtext">
            {t.bycontinuing}{" "}
            <a className="ref" href={USER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">{t.userAgreement}</a> {t.andAcknowledge}{" "}
            <a className="ref" href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">{t.privacyPolicy}</a>。
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <label className="auth-label">{t.email} *</label>
          <input
            type="email"
            name="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          {mode === "signin" && (
            <>
              <label className="auth-label">{t.password} *</label>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder={t.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </>
          )}

          <div className="auth-actions-row" style={{ marginTop: 8 }}>
            <button 
              type="submit" 
              disabled={loading || ((mode === "signup" || mode === "reset") && resendCountdown > 0)} 
              className="primary auth-submit-btn"
            >
              {loading ? t.pleaseWait : (
                mode === "signup" ? (
                  resendCountdown > 0 
                    ? `${t.sendVerification} (${resendCountdown}${t.seconds})`
                    : t.sendVerification
                ) : 
                mode === "reset" ? (
                  resendCountdown > 0
                    ? `${t.sendResetLink} (${resendCountdown}${t.seconds})`
                    : t.sendResetLink
                ) : t.logIn
              )}
            </button>

            {mode === "signin" && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setMode("reset"); }}
                className="auth-link"
              >
                {t.forgotPassword}
              </button>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="footer-action" style={{ marginTop: 12 }}>
          {mode === "signup" ? (
            <>
              <span>{t.alreadyHaveAccount}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setMode("signin"); }}
                className="footer-link"
              >
                {t.logIn}
              </button>
            </>
          ) : mode === "reset" ? (
            <>
              <span>{t.rememberedPassword}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setMode("signin"); }}
                className="footer-link"
              >
                {t.logIn}
              </button>
            </>
          ) : (
            <>
              <span>{t.newToSite}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setMode("signup"); }}
                className="footer-link"
              >
                {t.signUp}
              </button>
            </>
          )}
        </div>

        <div 
          className="result" 
          style={{ 
            marginTop: 12, 
            textAlign: "center",
            background: "transparent",
            border: "none",
            boxShadow: "none",
            color: messageType === "success" ? "#10b981" : messageType === "error" ? "#ef4444" : "inherit" 
          }}
        >
          {message}
        </div>
      </div>
    </div>
  );
}